/**
 * Pure mapping `JourneySnapshot → HintNote[]`.
 *
 * Extracted from the React hook so it can be exercised by Jest under
 * `testEnvironment: 'node'` without DOM/React. The hook (`use-journey-hints`)
 * is just a thin shell that calls this and pushes the result through
 * `useHintPublisher`.
 *
 * ## State → hint mapping
 *
 * | State        | Hand color(s)                            | Set                          |
 * |--------------|------------------------------------------|------------------------------|
 * | S0           | (handled by ChordPresentation)           | empty (see note below)       |
 * | S1a          | (handled by GripState)                   | empty (see note below)       |
 * | S1b          | (handled by PracticeState)               | empty (see note below)       |
 * | S2a          | (handled by GripState)                   | empty                        |
 * | S2b          | (handled by PracticeState)               | empty                        |
 * | S3           | (handled by PracticeState)               | empty                        |
 * | S4 / S5/DONE | (no hints)                               | empty                        |
 * | null         | (intro / phase 2+ / nothing)             | empty                        |
 *
 * ### Why empty in S0
 *
 * S0 used to light every note of the phrase at once. That contradicted
 * what the panel showed next to it: a list of chords with one of them
 * marked, while the keyboard displayed all of them merged. The state now
 * walks the progression one chord at a time (driven by playback position
 * or by the cell the player points at), and "which chord is current" is a
 * *view* concern, not an FSM one — the same reasoning as S1b/S2b/S3 below.
 * `ChordPresentation` therefore publishes its own hint under the
 * `'journey-s0'` source.
 *
 * ### Why empty in S1b/S2b/S3
 *
 * The "current group to play" only exists in `PracticeState` (via
 * `SequenceWatcher.currentIndex`). Lifting it into the FSM would require
 * tracking individual presses in `PhraseFSM`, out of its concerns. So
 * the journey hook returns `[]` for these states, and `PracticeState`
 * publishes a hint of its own under a separate `'journey-practice'`
 * source via `currentGroupHints` (below). The hint context's stack-of-
 * one-per-source means there is no conflict.
 *
 * ### Why no hints in S4/S5
 *
 * Spec §"Hints visuels en S4" : éteints. The player must rely on memory
 * and metronome, not on the visual cue, otherwise they "read the keys"
 * instead of internalizing the rhythm.
 */

import type { HintNote } from '@/contexts/hint-context';
import type { JourneySnapshot } from '@/lib/learning-journey/journey';

// ── Palette ───────────────────────────────────────────────────────

/**
 * Hand colors give the player a consistent mental map (cool teal = LH,
 * warm purple = RH).
 *
 * `*_DISCOVER` variants are slightly brighter to signal "this is the very
 * next note you're learning". The contrast stays subtle on purpose: the
 * focus comes from the lone highlight, not from a screaming color.
 */
export const JOURNEY_HINT_COLOR_LEFT_PRACTICE = 0x40d9c9;
export const JOURNEY_HINT_COLOR_RIGHT_PRACTICE = 0xb597ff;
export const JOURNEY_HINT_COLOR_LEFT_DISCOVER = 0x7df0e3;
export const JOURNEY_HINT_COLOR_RIGHT_DISCOVER = 0xd4baff;

/**
 * A key struck where the passage asks for nothing — the app's own
 * `--color-err`, and deliberately **not** a hand colour.
 *
 * The runs in time light the onset the playhead has reached in the colour of
 * the hand that owes it. A wrong key owes nothing to either hand, and painting
 * it turquoise or violet would say the opposite of what happened.
 */
export const JOURNEY_HINT_COLOR_STRAY = 0xef4444;

// ── Public API ────────────────────────────────────────────────────

/**
 * Compute the hint set for the given snapshot.
 *
 * Returns an empty array when:
 *   - `snapshot` is `null` (no journey yet),
 *   - we're outside `phase1` (no current phrase),
 *   - the current phrase state is `S4`, `S5` or `DONE`.
 */
export function computeJourneyHints(snapshot: JourneySnapshot | null): HintNote[] {
  if (snapshot === null) {
    return [];
  }

  const phrase = snapshot.currentPhrase;
  const phraseSnapshot = snapshot.currentPhraseSnapshot;

  if (phrase === null || phraseSnapshot === null) {
    return [];
  }

  switch (phraseSnapshot.state) {
    case 'S0':
      // Owned by `ChordPresentation` via the `'journey-s0'` source, which
      // publishes one chord at a time. See the header note.
      return [];

    case 'S1a':
    case 'S2a':
      // Owned by `GripState` under the `'journey-grip'` source. The step
      // runs in two acts and the first one deliberately lights nothing —
      // the player has to find the anchor among 88 keys. Which act we are
      // in is view state, not FSM state, so the FSM mapping stays out.
      return [];

    case 'S1b':
    case 'S2b':
    case 'S3':
      // Owned by `PracticeState` via `currentGroupHints`. The journey
      // hook stays out of the way to avoid two sources fighting.
      return [];

    case 'S4':
    case 'S5':
    case 'DONE':
      return [];
  }
}

/**
 * Hints for the *current group* of a practice run (S1b/S2b/S3).
 *
 * Pure helper called by `PracticeState`. The colour comes from the hand
 * carried by each note of the group, decided when the group was built.
 *
 * It used to be inferred here, by looking the midi up in the phrase's
 * whole right-hand note set — which painted a left-hand key right-hand as
 * soon as the right hand touched that key anywhere else in the unit. In S3
 * the colour *is* the instruction, and it was wrong on 4.4% of all (tick,
 * midi) pairs (12 617 of 285 655 over PDMX 450). The hand is a fact about
 * an instant; it now travels with the note instead of being re-derived.
 *
 * Returns an empty list if `group` is empty (defensive: should not
 * happen in practice, but the FSM may briefly be on a transition).
 */
export function currentGroupHints(group: {
  readonly notes: readonly { readonly midi: number; readonly hand: 'L' | 'R' }[];
}): HintNote[] {
  const out: HintNote[] = [];
  const seen = new Set<number>();

  for (const note of group.notes) {
    if (seen.has(note.midi)) {
      continue;
    }

    seen.add(note.midi);
    out.push({
      midi: note.midi,
      color:
        note.hand === 'R'
          ? JOURNEY_HINT_COLOR_RIGHT_PRACTICE
          : JOURNEY_HINT_COLOR_LEFT_PRACTICE,
    });
  }

  return out;
}

/**
 * Index of the onset **being played**, from the count of onsets gone by.
 *
 * `playedIndex` (see `use-rhythm-run`) counts the onsets the playhead has
 * gone *past*, so it names the one still to come: at the very instant a note
 * has to be struck, it already points at the next one. A run in time that lit
 * `groups[playedIndex]` therefore put two halos on the keyboard at once — the
 * key sounding, lit by the roll's own layer, and the key to come, lit in a
 * hand colour and wearing the note's name. The one carrying the instruction
 * was the wrong one, and the pair read as "play both".
 *
 * Before the first onset has gone by — at rest, or during the count-in — the
 * answer is the opening group: that is where the hands go before it starts.
 */
export function litOnsetIndex(playedIndex: number, count: number): number {
  if (count === 0) {
    return 0;
  }

  return Math.min(Math.max(playedIndex - 1, 0), count - 1);
}
